import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { environment } from '../../../../environments/environment';

// Global state to track script loading
let recaptchaScriptLoading: Promise<void> | null = null;

@Component({
  selector: 'app-google-recaptcha',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './google-recaptcha.component.html',
  styleUrl: './google-recaptcha.component.scss',
})
export class GoogleRecaptchaComponent implements AfterViewInit, OnDestroy {
  @Output() recaptchaToken = new EventEmitter<string>();

  @ViewChild('recaptchaDiv') recaptchaDiv!: ElementRef;

  isLoading = true;
  loadError = false;
  private widgetId: number | null = null;
  private renderAttempts = 0;
  private readonly MAX_RENDER_ATTEMPTS = 20; // Max 6 seconds (20 * 300ms)

  ngAfterViewInit(): void {
    this.loadRecaptchaScript()
      .then(() => {
        this.renderRecaptcha();
      })
      .catch((error) => {
        console.error('Failed to load reCAPTCHA script:', error);
        this.isLoading = false;
        this.loadError = true;
      });
  }

  ngOnDestroy(): void {
    // Clean up the reCAPTCHA widget when component is destroyed
    if (this.widgetId !== null) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((window as any).grecaptcha?.reset) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).grecaptcha.reset(this.widgetId);
        }
      } catch (error) {
        console.error('Error resetting reCAPTCHA:', error);
      }
    }
  }

  loadRecaptchaScript(): Promise<void> {
    // Use global promise to ensure only one script load happens
    if (recaptchaScriptLoading) {
      return recaptchaScriptLoading;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).grecaptcha) {
      return Promise.resolve();
    }

    recaptchaScriptLoading = new Promise((resolve, reject) => {
      const scriptId = 'recaptcha-script';
      const existingScript = document.getElementById(
        scriptId,
      ) as HTMLScriptElement;

      if (existingScript) {
        // Check if grecaptcha is already loaded
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((window as any).grecaptcha) {
          resolve();
          return;
        }

        // Wait for the existing script to load with a timeout
        const loadTimeout = setTimeout(() => {
          reject(new Error('reCAPTCHA script load timeout'));
        }, 10000);

        const onLoad = () => {
          clearTimeout(loadTimeout);
          existingScript.removeEventListener('error', onError);
          resolve();
        };

        const onError = () => {
          clearTimeout(loadTimeout);
          existingScript.removeEventListener('load', onLoad);
          reject(new Error('Failed to load reCAPTCHA script'));
        };

        existingScript.addEventListener('load', onLoad, { once: true });
        existingScript.addEventListener('error', onError, { once: true });
        return;
      }

      // Create new script element
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
      script.async = true;
      script.defer = true;

      const loadTimeout = setTimeout(() => {
        script.remove();
        recaptchaScriptLoading = null;
        reject(new Error('reCAPTCHA script load timeout'));
      }, 10000);

      script.onload = () => {
        clearTimeout(loadTimeout);
        resolve();
      };

      script.onerror = () => {
        clearTimeout(loadTimeout);
        script.remove();
        recaptchaScriptLoading = null;
        reject(new Error('Failed to load reCAPTCHA script'));
      };

      document.body.appendChild(script);
    });

    return recaptchaScriptLoading;
  }

  renderRecaptcha() {
    const tryRender = () => {
      this.renderAttempts++;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const grecaptcha = (window as any).grecaptcha;

      if (grecaptcha && this.recaptchaDiv) {
        try {
          // Check if grecaptcha.render is ready
          if (typeof grecaptcha.render === 'function') {
            this.widgetId = grecaptcha.render(this.recaptchaDiv.nativeElement, {
              sitekey: environment.googleReCaptchaSiteKey,
              callback: (token: string) => {
                this.recaptchaToken.emit(token);
              },
              'error-callback': () => {
                console.error('reCAPTCHA error occurred');
                this.loadError = true;
              },
            });
            this.isLoading = false;
            return;
          }
        } catch (error) {
          console.error('Error rendering reCAPTCHA:', error);
          this.isLoading = false;
          this.loadError = true;
        }
      }

      // Retry with limit
      if (this.renderAttempts < this.MAX_RENDER_ATTEMPTS) {
        setTimeout(tryRender, 300);
      } else {
        console.error(
          'Failed to render reCAPTCHA after maximum attempts. Please reload the page.',
        );
        this.isLoading = false;
        this.loadError = true;
      }
    };

    tryRender();
  }
}
